// Daily Performance test list ("/app/daily-performance") — a deliberate,
// fully isolated clone of Tests.jsx pointed at the /daily-performance API
// instead of /tests. Students see published tests with their own attempt
// status; teachers/admins see their own tests (with an attempt_count) plus
// create/delete actions. Authoring questions happens in
// DailyPerformanceBuilder.jsx; taking a test happens in TakeDailyPerformance.jsx.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Plus, Trash2, Clock } from "lucide-react";

export default function DailyPerformance() {
  const { user } = useAuth();
  const [tests, setTests] = useState([]);
  const isTeacher = user.role !== "student";

  const load = () => api.get("/daily-performance").then((r) => setTests(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/daily-performance/${id}`);
      toast.success("Daily Performance test deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <h1 className="font-heading text-3xl font-black tracking-tight">Daily Performance</h1>
        {isTeacher && (
          <Link to="/app/daily-performance/new" data-testid="create-daily-performance-button" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-blue-700 text-white hover:bg-blue-900 transition-colors">
            <Plus className="w-4 h-4" /> Create test
          </Link>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {tests.map((t) => (
          <div key={t.id} className="border border-zinc-200 p-6 hover:border-zinc-300 hover:shadow-sm transition-all" data-testid={`daily-performance-card-${t.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-[0.15em] font-semibold text-blue-700">{t.subject}</span>
                  {t.course_name ? (
                    <span className="text-[10px] uppercase tracking-[0.1em] font-bold bg-zinc-950 text-white px-1.5 py-0.5" data-testid={`daily-performance-course-badge-${t.id}`}>{t.course_name}</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-[0.1em] font-bold bg-blue-50 text-blue-700 border border-blue-200 px-1.5 py-0.5" data-testid={`daily-performance-all-badge-${t.id}`}>For all students</span>
                  )}
                </div>
                <h3 className="font-heading font-bold mt-1">{t.title}</h3>
              </div>
              <BarChart3 className="w-5 h-5 text-zinc-300 shrink-0" />
            </div>
            <div className="flex items-center gap-4 text-xs text-zinc-500 mt-3">
              <span>{t.questions?.length ?? 0} questions</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{t.duration_min} min</span>
              <span>{t.total_marks} marks</span>
            </div>
            <div className="mt-5">
              {isTeacher ? (
                <div className="flex items-center justify-between">
                  <Link to={`/app/daily-performance/${t.id}/results`} data-testid={`view-results-${t.id}`} className="text-sm font-semibold text-blue-700 hover:underline">
                    {t.attempt_count} attempts — View results →
                  </Link>
                  <div className="flex gap-1">
                    <Link to={`/app/daily-performance/${t.id}/edit`} data-testid={`edit-daily-performance-${t.id}`} className="px-3 py-2 text-xs font-semibold border border-zinc-300 hover:bg-zinc-100 transition-colors">
                      Modify
                    </Link>
                    <button onClick={() => remove(t.id)} data-testid={`delete-daily-performance-${t.id}`} className="p-2 border border-zinc-300 text-zinc-500 hover:text-red-600 hover:border-red-300 transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ) : t.my_attempt ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between border border-green-200 bg-green-50 px-4 py-2.5">
                    <span className="text-sm font-semibold text-green-700">{t.retakes_allowed ? "Latest attempt" : "Attempted"}</span>
                    <span className="text-sm font-bold text-green-700" data-testid={`daily-performance-score-${t.id}`}>{t.my_attempt.score} / {t.my_attempt.total}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Link to={`/app/daily-performance/${t.id}/review`} data-testid={`review-daily-performance-${t.id}`} className="block text-center py-2 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100 transition-colors">
                      Review answers
                    </Link>
                    <Link to={`/app/daily-performance/${t.id}/leaderboard`} data-testid={`leaderboard-daily-performance-${t.id}`} className="block text-center py-2 text-sm font-semibold border border-zinc-300 hover:bg-zinc-100 transition-colors">
                      Leaderboard
                    </Link>
                  </div>
                  {t.retakes_allowed && (
                    <Link to={`/app/daily-performance/${t.id}/take`} data-testid={`retake-daily-performance-${t.id}`} className="block text-center py-2.5 text-sm font-semibold bg-blue-700 text-white hover:bg-blue-900 transition-colors">
                      Retake test
                    </Link>
                  )}
                </div>
              ) : (
                <Link to={`/app/daily-performance/${t.id}/take`} data-testid={`start-daily-performance-${t.id}`} className="block text-center py-2.5 text-sm font-semibold bg-blue-700 text-white hover:bg-blue-900 transition-colors">
                  Start test
                </Link>
              )}
            </div>
          </div>
        ))}
      </div>
      {tests.length === 0 && <p className="text-sm text-zinc-500 py-8" data-testid="daily-performance-empty-state">{isTeacher ? "You haven't created any Daily Performance tests yet." : "No Daily Performance tests published yet."}</p>}
    </div>
  );
}
